import apiClient from '@/lib/apiClient';
import type { FormStructure } from '@/lib/formTypes';

export type ScheduleExceptionKind = 'ClosedAllDay' | 'CustomHours';

export type TenantScheduleException = {
  date: string;
  kind: ScheduleExceptionKind;
  openTime: string | null;
  closeTime: string | null;
  description: string | null;
};

export type UpdateTenantSettingsScheduleException = {
  date: string;
  kind: ScheduleExceptionKind;
  openTime: string | null;
  closeTime: string | null;
  description: string | null;
};

export type TenantSettings = {
  primaryColor: string;
  secondaryColor: string;
  currencySymbol: string;
  logoUrl: string | null;
  companyName: string;
  companyAddress: string | null;
  googleMapsLocation?: string | null;
  companyPhone: string | null;
  companyEmail: string | null;
  businessOpenTime: string;
  businessCloseTime: string;
  scheduleExceptions: TenantScheduleException[];
};

export type UpdateTenantSettingsRequest = {
  // Theme
  primaryColor?: string | null;
  secondaryColor?: string | null;
  currencySymbol?: string | null;

  // Company
  companyName?: string | null;
  companyAddress?: string | null;
  googleMapsLocation?: string | null;
  companyPhone?: string | null;
  companyEmail?: string | null;

  // Availability (omit/null → unchanged; scheduleExceptions: [] clears all)
  businessOpenTime?: string | null;
  businessCloseTime?: string | null;
  scheduleExceptions?: UpdateTenantSettingsScheduleException[] | null;
};

export async function getTenantSettings() {
  const res = await apiClient.get<TenantSettings>('/settings');
  return res.data;
}

export async function updateTenantSettings(request: UpdateTenantSettingsRequest) {
  const res = await apiClient.put<TenantSettings>('/settings', request);
  return res.data;
}

export async function uploadTenantLogo(file: File) {
  const formData = new FormData();
  formData.append('file', file);

  const res = await apiClient.put<TenantSettings>('/settings/logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  return res.data;
}

export type AdvanceOptions = {
  rentalNotesFormStructureJson: FormStructure | null;
  customerNotesFormStructureJson: FormStructure | null;
};

export type UpdateAdvanceOptionsRequest = {
  rentalNotesFormStructureJson?: FormStructure | null;
  customerNotesFormStructureJson?: FormStructure | null;
};

export async function getAdvanceOptions(): Promise<AdvanceOptions> {
  const res = await apiClient.get<AdvanceOptions>('/settings/advance-options');
  return res.data;
}

export async function updateAdvanceOptions(request: UpdateAdvanceOptionsRequest): Promise<AdvanceOptions> {
  const res = await apiClient.put<AdvanceOptions>('/settings/advance-options', request);
  return res.data;
}

